import { useEffect, useState } from "react";
import { DoctorDto } from "@apiModels/DoctorDto.csharp";
import { LocationDto } from "@apiModels/LocationDto.csharp";
import { ServiceDto } from "@apiModels/ServiceDto.csharp";
import { BookingOption } from "@apiModels/BookingOption.csharp";
import { MissingAppointmentsDto } from "@apiModels/MissingAppointments/MissingAppointmentsDto.csharp";
import {GtmService} from "../GtmService";
import {SpecializationDto} from "@apiModels/SpecializationDto.csharp";

export const useMissingAppointmentsMessage = (
  doctor: DoctorDto,
  location: LocationDto,
  specialization: SpecializationDto,
  service: ServiceDto,
  missingAppointments: MissingAppointmentsDto,
  allLocations: LocationDto[]
) => {
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!!message) {
      GtmService.bookingNoOnlineBooking(
          specialization?.name,
          specialization?.id,
          service?.bookingName,
          service?.id,
          location?.name,
          location?.id,
          message
      );
    }
  }, [message])

  const serviceNoOnlineBookingInfo = service.noOnlineBookingInfo;

  const getCenterLinks = (centers: LocationDto[]) => {
    return centers
      .map((c) => `<a href="${c.contactFormUrl}">${c.name}</a>`)
      .join(" / ");
  };

  useEffect(() => {
    const bookingOption = service.centerBookingOptions?.find(
      (cbo) => cbo.centerId == location.id
    );

    const serviceNotAvailableForOnlineBooking =
      service.availableForOnlineBooking == false;

    const centerNotSupportingBooking =
      location.isLocation == false && location.supportsOnlineBooking == false;
    const serviceIsOfflineOnCenter =
      bookingOption && bookingOption.bookingOption != BookingOption.Online;

    const serviceCenterIds = service.centerBookingOptions.map(
      (cbo) => cbo.centerId
    );
    const centers = allLocations.filter(
      (loc) =>
        location.centers.includes(loc.id) && serviceCenterIds.includes(loc.id)
    );

    const locationWithoutOnlineBookingMessage =
      missingAppointments.locationWithoutOnlineBookingMessage
        ?.replaceAll("$locationName", location.name)
        .replaceAll("$centerLinks", getCenterLinks(centers)) ?? "";

    if (
      serviceNotAvailableForOnlineBooking ||
      centerNotSupportingBooking ||
      serviceIsOfflineOnCenter
    ) {
      const newMessage =
        bookingOption?.noOnlineBookingInfo ?? serviceNoOnlineBookingInfo;

      if (!!newMessage) {
        setMessage(newMessage);
        return;
      }

      if (location.id && location.id != "-1") {
        if (location.isLocation) {
          setMessage(locationWithoutOnlineBookingMessage);
          return;
        } else {
          const generalMessage =
            missingAppointments.centerWithoutOnlineBookingMessage
              ?.replaceAll("$centerName", location.name)
              .replaceAll("$centerLink", getCenterLinks([location])) ?? "";
          setMessage(generalMessage);
          return;
        }
      }
      const centers = allLocations.filter((loc) =>
        service.centerBookingOptions.map((cbo) => cbo.centerId).includes(loc.id)
      );
      const generalMessage =
        missingAppointments.serviceWithoutOnlineBookingMessage
          ?.replaceAll("$centerLinks", getCenterLinks(centers)) ?? "";
      setMessage(generalMessage);
      return;
    }

    const missingBoth = missingAppointments.doctorsAndCenters.some(
      (pair) => pair.centerId == location?.id && pair.doctorId == doctor?.id
    );

    if (missingBoth && !doctor.ignoreMissingAppointmentsStatistics && !location.ignoreMissingAppointmentsStatistics) {
      const newMessage =
        missingAppointments.doctorAndCenterMessage
          ?.replaceAll("$centerName", getCenterLinks([location]))
          .replaceAll("$doctorName", doctor.name) ?? "";
      setMessage(newMessage);
      return;
    }

    const missingDoctor = missingAppointments.doctorIds.some(
      (d) => d == doctor?.id
    );

    if (
      (missingDoctor || doctor.isNotADoctor) &&
      !doctor.ignoreMissingAppointmentsStatistics
    ) {
      const centers = allLocations.filter((loc) =>
        doctor.centers.includes(loc.id)
      );

      const newMessage =
        missingAppointments.doctorMessage
          ?.replaceAll("$doctorName", doctor.name)
          .replaceAll("$centerLinks", getCenterLinks(centers)) ?? "";
      setMessage(newMessage);
      return;
    }

    const missingCenter = missingAppointments.centerIds.some(
      (d) => d == location?.id
    );

    if (!location.ignoreMissingAppointmentsStatistics && missingCenter) {
      const newMessage =
        missingAppointments.centerMessage
          ?.replaceAll("$centerName", location.name)
          .replaceAll("$centerLink", getCenterLinks([location])) ?? "";

      setMessage(newMessage);
      return;
    }

    const allCentersInLocationDisabled =
      location.isLocation &&
      service.centerBookingOptions
        .filter((x) => location.centers.includes(x.centerId))
        .every((x) => x.bookingOption != BookingOption.Online);
    if (allCentersInLocationDisabled) {
      setMessage(
        serviceNoOnlineBookingInfo ?? locationWithoutOnlineBookingMessage
      );
      return;
    }

    setMessage("");
  }, [doctor, location, missingAppointments]);

  return {
    missingAppointmentsMessage: message,
  };
};
